import React, { useContext, useEffect, useState } from 'react';
import { Context } from '../../../App';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Zoom from '@mui/material/Zoom';
import InputAdornment from '@mui/material/InputAdornment';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MuiSnackbar from '../../common/MuiSnackbar';
import CircularProgress from '@mui/material/CircularProgress';
import SeoHelmet from '../../common/SeoHelmet';
import CalculateButton from '../../common/calculateButton';
import SaveButton from '../../common/saveButton';
import Header from '../../common/Header';
import Footer from '../../common/Footer';
import Sidebar from '../../sidebar/Sidebar';
import DecisionModal from '../../modals/DecisionModal';
import PricingInfoModal from '../../modals/PricingInfoModal';
import UscsClassifierResult from './UscsClassifierResult';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Button from '@mui/material/Button';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useCalculationContext, CalculationContextProvider } from '../../context/calculationContext';
import useLoadSavedCalculation from '../useLoadSavedCalculation';

const StoneColumnEcgm = () => {
  const { user } = useContext(Context);
  const {
    MAX_NAME_LENGTH,
    isLoadingCalculate,
    isLoadingLoadAll,
    isLoadingLoad,
    isLoadingSave,
    result,
    calculations,
    lastData,
    showOutput,
    setShowOutput,
    calculateButtonDisabled,
    setCalculateButtonDisabled,
    saveButtonDisabled,
    setSaveButtonDisabled,
    errorMessage,
    displaySidebar,
    setDisplaySidebar,
    decisionModalIsOpen,
    decision,
    setDecision,
    handleSidebarDisplay,
    setDecisionModalIsOpenToFalse,
    showMuiSnack,
    open,
    setOpen,
    alertMessage,
    severity,
    onChangeWarningForPositiveDecimals,
    calculate,
    loadOneCalculation,
    loadAllCalculations,
    update,
    checkCalculationNameforSaving,
    checkCalculationNameforSavingAs,
    deleteCalculations,
    calculationCountReached,
    saveCountReached,
  } = useCalculationContext();

  //SET STATES FOR INPUT VARIABLES
  const [name, setName] = useState('');
  const [percentPassing200, setPercentPassing200] = useState('');
  const [percentPassing4, setPercentPassing4] = useState('');
  const [liquidLimit, setLiquidLimit] = useState('');
  const [plasticLimit, setPlasticLimit] = useState('');
  const [D10, setD10] = useState('');
  const [D30, setD30] = useState('');
  const [D60, setD60] = useState('');
  const [isOrganic, setIsOrganic] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [showPdfWarningModal, setShowPdfWarningModal] = useState(false);

  // SET THE LOADED INPUTS
  const setInputs = (inputs) => {
    setName(inputs.name);
    setPercentPassing200(inputs.percentPassing200);
    setPercentPassing4(inputs.percentPassing4);
    setLiquidLimit(inputs.liquidLimit);
    setPlasticLimit(inputs.plasticLimit);
    setD10(inputs.D10);
    setD30(inputs.D30);
    setD60(inputs.D60);
    setIsOrganic(inputs.isOrganic);
  };

  //INPUTS FOR CALCULATION
  const calculationInput = {
    name,
    percentPassing200,
    percentPassing4,
    liquidLimit,
    plasticLimit,
    D10,
    D30,
    D60,
    isOrganic,
  };

  // MANAGE SAVE & CALCULATE BUTTONS DISABLED/ENABLED STATES
  useEffect(() => {
    setCalculateButtonDisabled(false);

    if (
      (calculationInput.name || calculationInput.name === 0) &&
      (calculationInput.percentPassing200 || calculationInput.percentPassing200 === 0) &&
      (calculationInput.percentPassing4 || calculationInput.percentPassing4 === 0) &&
      (calculationInput.liquidLimit || calculationInput.liquidLimit === 0) &&
      (calculationInput.plasticLimit || calculationInput.plasticLimit === 0) &&
      (calculationInput.D10 || calculationInput.D10 === 0) &&
      (calculationInput.D30 || calculationInput.D30 === 0) &&
      (calculationInput.D60 || calculationInput.D60 === 0)
    ) {
      setCalculateButtonDisabled(false);
    } else {
      setCalculateButtonDisabled(false);
    }
  }, [
    setCalculateButtonDisabled,
    calculationInput.name,
    calculationInput.percentPassing200,
    calculationInput.percentPassing4,
    calculationInput.liquidLimit,
    calculationInput.plasticLimit,
    calculationInput.D10,
    calculationInput.D30,
    calculationInput.D60,
    calculationInput.isOrganic,
  ]);

  useEffect(() => {
    setSaveButtonDisabled(true);

    if (calculationInput.name || calculationInput.name === 0) {
      setSaveButtonDisabled(false);
    } else {
      setSaveButtonDisabled(true);
    }
  }, [setSaveButtonDisabled, calculationInput.name]);

  // Do not render output if any input parameter except name changes
  useEffect(() => {
    setShowOutput(false);
  }, [
    setShowOutput,
    calculationInput.percentPassing200,
    calculationInput.percentPassing4,
    calculationInput.liquidLimit,
    calculationInput.plasticLimit,
    calculationInput.D10,
    calculationInput.D30,
    calculationInput.D60,
    calculationInput.isOrganic,
  ]);

  //CLEAR INPUTS AND CLEAR OUTPUT
  const createNewCalculation = () => {
    setPercentPassing200('');
    setPercentPassing4('');
    setLiquidLimit('');
    setPlasticLimit('');
    setD10('');
    setD30('');
    setD60('');
    setIsOrganic(false);
    setShowOutput(false);
  };

  //API URLS
  const URL = {
    CALCULATE: '/api/v1/calculations/uscsClassifier',
    LOADONE: `/api/v1/calculations/uscsClassifier/loadOne/`,
    LOADALL: '/api/v1/calculations/uscsClassifier/loadAll',
    UPDATE: `/api/v1/calculations/uscsClassifier/update/`,
    SAVE: '/api/v1/calculations/uscsClassifier/save',
    CHECKUNIQUENAME: `/api/v1/calculations/uscsClassifier/checkUniqueName/`,
    DELETE: '/api/v1/calculations/uscsClassifier/delete',
  };
  useLoadSavedCalculation({ URL, loadOneCalculation, calculate, setInputs, showMuiSnack });

  const handleChangeName = (e) => {
    if (e.target.value.length >= MAX_NAME_LENGTH) {
      showMuiSnack(`Calculation name can be max ${MAX_NAME_LENGTH} characters.`, 'warning');
    }
    setName(e.target.value);
  };

  //TABS
  const [value, setValue] = useState(0);
  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleExportPdf = async () => {
    if (isExporting || !showOutput) return;
    if (user?.role === 'basic' || user?.role === 'standard') {
      setShowPdfWarningModal(true);
      return;
    }
    setIsExporting(true);
    try {
      const payload = { ...calculationInput };

      const res = await fetch('/api/v1/pdf/uscsClassifier', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        let serverMessage = '';
        try {
          const errorJson = await res.json();
          serverMessage = errorJson?.message || errorJson?.error || JSON.stringify(errorJson);
        } catch (e) {
          serverMessage = await res.text();
        }
        throw new Error(`PDF generation failed (${res.status}). ${serverMessage || 'No server message.'}`);
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'geocalc-uscs-classifier-report.pdf';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showMuiSnack(err.message || 'PDF generation failed. Please try again.', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <SeoHelmet
        title="GeoCalc | USCS Soil Classifier (ASTM D2487-17)"
        description="Classify soils per ASTM D2487-17: enter P200/P4, LL/PL, D10–D60, and organic flag to get the USCS symbol, group name, and gradation indices."
        url="https://www.geocalc.app/calculations/uscsClassifier"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="tool"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'USCS Soil Classifier', url: 'https://www.geocalc.app/calculations/uscsClassifier' },
        ]}
      />

      <Header />
      <div className="container">
        <MuiSnackbar open={open} setOpen={setOpen} severity={severity} alertMessage={alertMessage} />
        {displaySidebar ? (
          <Sidebar
            URL={URL}
            loadAllCalculations={loadAllCalculations}
            calculations={calculations}
            loadOneCalculation={loadOneCalculation}
            calculate={calculate}
            setInputs={setInputs}
            deleteCalculations={deleteCalculations}
            createNewCalculation={createNewCalculation}
            isLoading={isLoadingLoadAll}
            handleSidebarDisplay={handleSidebarDisplay}
            displaySidebar={displaySidebar}
            setDisplaySidebar={setDisplaySidebar}
          />
        ) : (
          <MenuOpenIcon
            color="primary"
            fontSize="large"
            className="sidebar-open-icon"
            alt="side bar menu icon"
            label="Show my calculations"
            onClick={handleSidebarDisplay}
          />
        )}
        <DecisionModal
          URL={URL}
          data={calculationInput}
          decisionModalIsOpen={decisionModalIsOpen}
          setDecisionModalIsOpenToFalse={setDecisionModalIsOpenToFalse}
          decision={decision}
          setDecision={setDecision}
          checkCalculationNameforSavingAs={checkCalculationNameforSavingAs}
          name={name}
          setName={setName}
          errorMessage={errorMessage}
          update={update}
          saveButtonDisabled={saveButtonDisabled}
        />
        <PricingInfoModal open={showPdfWarningModal} setOpen={setShowPdfWarningModal} role={user?.role} />

        <div className="right-calculation">
          <div className="uscsContainer">
            <h1>USCS Soil Classifier</h1>
            <div className="uscsTabContainer">
              <Tabs value={value} onChange={handleChange} variant="scrollable" scrollButtons allowScrollButtonsMobile>
                <Tab label="Sieve Results" sx={{ textTransform: 'none' }} />
                <Tab label="Atterberg Limits" sx={{ textTransform: 'none' }} />
                <Tab label="Particle Size Distribution" sx={{ textTransform: 'none' }} />
                <Tab label="Organic Matter" sx={{ textTransform: 'none' }} />
              </Tabs>
            </div>
            {value === 0 && (
              <div className="uscsInputs">
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label="Name"
                  variant="outlined"
                  inputProps={{
                    maxLength: `${MAX_NAME_LENGTH}`,
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={`Your calculation name (Max. ${MAX_NAME_LENGTH} characters)`}
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={name}
                  onChange={handleChangeName}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label="P200 (%)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Required: Percentage of soil particles passing the No. 200 sieve (0.075 mm)"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={percentPassing200}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'P200(%)', setPercentPassing200)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  style={{ minWidth: 80, maxWidth: 120 }}
                  size="small"
                  label="P4 (%)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title="Required: Percentage of soil particles passing the No. 4 sieve (4.75 mm)"
                          arrow
                          placement="bottom"
                          TransitionComponent={Zoom}
                        >
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={percentPassing4}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'P4(%)', setPercentPassing4)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 1 && (
              <div className="uscsInputs">
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label="LL (%)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Liquid Limit of the soil" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={liquidLimit}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Liquid Limit', setLiquidLimit)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label="PL (%)"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Plastic Limit of the soil" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={plasticLimit}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'Plastic Limit', setPlasticLimit)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 2 && (
              <div className="uscsInputs">
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label="D10"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Particle size for 10% passing" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={D10}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'D10', setD10)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label="D30"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Particle size for 30% passing" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={D30}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'D30', setD30)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
                <TextField
                  size="small"
                  style={{ minWidth: 80, maxWidth: 120 }}
                  label="D60"
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Particle size for 60% passing" arrow placement="bottom" TransitionComponent={Zoom}>
                          <InfoTwoToneIcon fontSize={'string'} />
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                  value={D60}
                  onChange={(e) => onChangeWarningForPositiveDecimals(e, 'D60', setD60)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                />
              </div>
            )}
            {value === 3 && (
              <div className="uscsInputs">
                <TextField
                  style={{ minWidth: 140, maxWidth: 160 }}
                  label={'Organic ?'}
                  size="small"
                  select
                  value={isOrganic}
                  onChange={(e) => setIsOrganic(e.target.value)}
                  disabled={isLoadingCalculate || isLoadingLoad || isLoadingSave}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>
              </div>
            )}

            <div className="buttonContainer uscsSaveCalculateButtonContainer">
              <CalculateButton
                calculationCountReached={calculationCountReached}
                calculate={calculate}
                calculationInput={calculationInput}
                URL={URL}
                calculateButtonDisabled={calculateButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
              <Button
                variant="contained"
                startIcon={!isExporting ? <PictureAsPdfIcon /> : null}
                onClick={handleExportPdf}
                disabled={!showOutput || isExporting}
                style={{ width: '200px' }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={18} style={{ marginRight: 8 }} />
                    Creating PDF...
                  </>
                ) : (
                  'Create PDF'
                )}
              </Button>

              <SaveButton
                saveCountReached={saveCountReached}
                checkCalculationNameforSaving={checkCalculationNameforSaving}
                calculationInput={calculationInput}
                URL={URL}
                saveButtonDisabled={saveButtonDisabled}
                isLoadingCalculate={isLoadingCalculate}
                isLoadingLoad={isLoadingLoad}
                isLoadingSave={isLoadingSave}
              />
            </div>
          </div>
          {showOutput && <UscsClassifierResult data={lastData} result={result} />}
          {isLoadingLoad && (
            <div className="centerLoading">
              <CircularProgress size={30} />
            </div>
          )}
          {isLoadingCalculate && (
            <div className="centerLoading">
              <CircularProgress size={30} />
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const StoneColumnEcgmWithProvider = () => {
  return (
    <CalculationContextProvider>
      <StoneColumnEcgm />
    </CalculationContextProvider>
  );
};
export default StoneColumnEcgmWithProvider;
